import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import AppShell from './components/Layout/AppShell';
import { AppPage } from './components/Layout/Sidebar';
import { DS } from './theme';
import LandingPage from './pages/LandingPage';
import DashboardPage from './pages/DashboardPage';
import PricingPage from './pages/PricingPage';
import DemoDatasetPage from './pages/DemoDatasetPage';
import AdminSetupPage from './AdminSetupPage';
import ProjectManager from './ProjectManager';
import DatasetManager from './DatasetManager';
import PasswordChange from './PasswordChange';
import ExplorePage from './pages/ExplorePage';

interface User {
    id: number;
    username: string;
    role: string;
}

interface Project {
    id: number;
    name: string;
    user_id: number;
    created_at: string;
    dataset_count: number;
}

interface Dataset {
    id: number;
    name: string;
    table_name: string;
    project_id: number;
    uploaded_at: string;
}

// ─────────────────────────────────────────────
// LOGIN PAGE
// ─────────────────────────────────────────────
const LoginPage: React.FC<{
    onLogin: (user: User) => void;
    onBack: () => void;
    onDemo?: () => void;
    onSignUp?: () => void;
    defaultRegister?: boolean;
}> = ({ onLogin, onBack, onDemo, onSignUp, defaultRegister = false }) => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isRegistering, setIsRegistering] = useState(defaultRegister);
    const [error, setError] = useState('');
    const [networkError, setNetworkError] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setNetworkError(false);
        try {
            const response = await fetch('/api/login', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ username, password }),
            });
            if (response.ok) {
                const meRes = await fetch('/api/me', { credentials: 'include' });
                if (meRes.ok) {
                    const user = await meRes.json();
                    onLogin(user);
                } else {
                    setError('Login succeeded but could not load your account.');
                }
            } else {
                const data = await response.json();
                setError(data.detail || 'Incorrect username or password.');
            }
        } catch {
            setNetworkError(true);
        } finally {
            setLoading(false);
        }
    };

    const handleRegister = async (e: React.FormEvent) => {
        e.preventDefault();
        if (password !== confirmPassword) {
            setError('Passwords do not match.');
            return;
        }
        setLoading(true);
        setError('');
        setNetworkError(false);
        try {
            const response = await fetch('/api/register', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ username, password }),
            });
            if (response.ok) {
                const loginRes = await fetch('/api/login', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify({ username, password }),
                });
                if (loginRes.ok) {
                    const meRes = await fetch('/api/me', { credentials: 'include' });
                    if (meRes.ok) onLogin(await meRes.json());
                }
            } else {
                const data = await response.json();
                setError(data.detail || 'Registration failed.');
            }
        } catch {
            setNetworkError(true);
        } finally {
            setLoading(false);
        }
    };

    const handleGuestAccess = async () => {
        try {
            const res = await fetch('/api/guest-access', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
            });
            if (res.ok) {
                const data = await res.json();
                if (data.user || data.session) {
                    onLogin(data.user || { username: 'Guest', role: 'guest' });
                    return;
                }
            }
        } catch {
            // fall through to demo
        }
        if (onDemo) onDemo();
    };

    return (
        <Box sx={{ minHeight: '100vh', backgroundColor: DS.bg, display: 'flex', flexDirection: 'column' }}>
            {/* Top bar */}
            <Box sx={{ height: 56, display: 'flex', alignItems: 'center', px: 3, backgroundColor: DS.bg2, borderBottom: `1px solid ${DS.border}` }}>
                <Button startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />} onClick={onBack} sx={{ color: DS.textMuted, fontSize: '0.875rem' }}>
                    Back
                </Button>
                <Box sx={{ flex: 1 }} />
                <Box onClick={onBack} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer', opacity: 1, '&:hover': { opacity: 0.8 } }}>
                    <Box sx={{ width: 26, height: 26, backgroundColor: DS.accent, borderRadius: '7px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography sx={{ color: '#fff', fontSize: '10px', fontWeight: 700 }}>DS</Typography>
                    </Box>
                    <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, color: DS.text, letterSpacing: '-0.3px' }}>DataScope</Typography>
                </Box>
            </Box>

            {/* Form */}
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', px: 3, py: 6 }}>
                <Box sx={{ width: '100%', maxWidth: 400 }}>
                    <Typography sx={{ fontSize: '1.5rem', fontWeight: 600, color: DS.text, letterSpacing: '-0.03em', mb: 0.5 }}>
                        {isRegistering ? 'Create your account' : 'Welcome back'}
                    </Typography>
                    <Typography sx={{ fontSize: '0.875rem', color: DS.textMuted, mb: 4 }}>
                        {isRegistering ? 'Start analyzing your data in minutes.' : 'Sign in to access your projects and datasets.'}
                    </Typography>

                    {!isRegistering && (
                        <>
                            <Button fullWidth variant="outlined" onClick={handleGuestAccess} disabled={loading}
                                sx={{ py: 1.25, mb: 2 }}>
                                {loading ? 'Loading…' : 'Continue as Guest'}
                            </Button>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                                <Divider sx={{ flex: 1 }} />
                                <Typography sx={{ fontSize: '0.75rem', color: DS.textFaint }}>or</Typography>
                                <Divider sx={{ flex: 1 }} />
                            </Box>
                        </>
                    )}

                    <form onSubmit={isRegistering ? handleRegister : handleLogin}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <TextField label="Username" fullWidth value={username} onChange={e => setUsername(e.target.value)} autoFocus required size="medium" />
                            <TextField
                                label="Password" type={showPassword ? 'text' : 'password'} fullWidth value={password}
                                onChange={e => setPassword(e.target.value)} required size="medium"
                                InputProps={{ endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton size="small" onClick={() => setShowPassword(!showPassword)} edge="end">
                                            {showPassword ? <VisibilityOff sx={{ fontSize: 18 }} /> : <Visibility sx={{ fontSize: 18 }} />}
                                        </IconButton>
                                    </InputAdornment>
                                )}}
                            />
                            {isRegistering && (
                                <TextField
                                    label="Confirm password" type={showConfirmPassword ? 'text' : 'password'} fullWidth value={confirmPassword}
                                    onChange={e => setConfirmPassword(e.target.value)} required size="medium"
                                    InputProps={{ endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton size="small" onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
                                                {showConfirmPassword ? <VisibilityOff sx={{ fontSize: 18 }} /> : <Visibility sx={{ fontSize: 18 }} />}
                                            </IconButton>
                                        </InputAdornment>
                                    )}}
                                />
                            )}
                        </Box>

                        {networkError && (
                            <Box sx={{ mt: 2, p: 2, bgcolor: DS.amberDim, border: `1px solid ${DS.amberBorder}`, borderRadius: '10px' }}>
                                <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: DS.amber, mb: 0.5 }}>Server not reachable</Typography>
                                <Typography sx={{ fontSize: '0.78rem', color: DS.textMuted, lineHeight: 1.5 }}>
                                    The backend isn't available right now. You can still explore the app with sample data.
                                </Typography>
                                {onDemo && (
                                    <Button size="small" variant="contained" onClick={onDemo}
                                        sx={{ mt: 1.25, borderRadius: '8px', fontWeight: 600, fontSize: '0.8rem', px: 2, py: 0.625 }}>
                                        Try Demo Mode →
                                    </Button>
                                )}
                            </Box>
                        )}
                        {error && !networkError && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

                        <Button type="submit" variant="contained" fullWidth disabled={loading}
                            endIcon={loading ? <CircularProgress size={16} sx={{ color: '#FFFFFF' }} /> : <ArrowForwardIcon />}
                            sx={{ mt: 3, py: 1.375, fontSize: '0.9375rem', fontWeight: 600 }}>
                            {loading ? '' : isRegistering ? 'Create Account' : 'Sign In'}
                        </Button>
                    </form>

                    <Box sx={{ mt: 3, textAlign: 'center' }}>
                        {isRegistering ? (
                            <>
                                <Typography sx={{ fontSize: '0.875rem', color: DS.textMuted, display: 'inline' }}>
                                    Already have an account?{' '}
                                </Typography>
                                <Button variant="text" size="small"
                                    onClick={() => { setIsRegistering(false); setError(''); setNetworkError(false); setConfirmPassword(''); }}
                                    sx={{ color: DS.text, fontWeight: 600, p: 0, minWidth: 'auto', fontSize: '0.875rem' }}>
                                    Sign in
                                </Button>
                            </>
                        ) : (
                            <>
                                <Typography sx={{ fontSize: '0.875rem', color: DS.textMuted, display: 'inline' }}>
                                    Don't have an account?{' '}
                                </Typography>
                                <Button variant="text" size="small" onClick={onSignUp}
                                    sx={{ color: DS.accent, fontWeight: 600, p: 0, minWidth: 'auto', fontSize: '0.875rem' }}>
                                    Sign up free →
                                </Button>
                            </>
                        )}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

// ─────────────────────────────────────────────
// PAGE CONTENT WRAPPER
// ─────────────────────────────────────────────
const PageContent: React.FC<{ children: React.ReactNode; title?: string; subtitle?: string }> = ({ children, title, subtitle }) => (
    <Box sx={{ p: { xs: 3, md: '40px 48px 60px' }, maxWidth: 1200, width: '100%' }}>
        {(title || subtitle) && (
            <Box sx={{ mb: 4, pb: 3, borderBottom: `1px solid ${DS.border}` }}>
                {title && <Typography sx={{ fontSize: '1.375rem', fontWeight: 600, color: DS.text, letterSpacing: '-0.03em', mb: subtitle ? 0.5 : 0 }}>{title}</Typography>}
                {subtitle && <Typography sx={{ fontSize: '0.875rem', color: DS.textMuted }}>{subtitle}</Typography>}
            </Box>
        )}
        {children}
    </Box>
);

// ─────────────────────────────────────────────
// LANDING ROUTE — both CTAs go to /login
// ─────────────────────────────────────────────
const LandingRoute: React.FC = () => {
    const navigate = useNavigate();
    return (
        <LandingPage
            onStart={() => navigate('/login')}
            onGuestAccess={() => navigate('/login')}
        />
    );
};

// ─────────────────────────────────────────────
// PRICING ROUTE
// ─────────────────────────────────────────────
const PricingRoute: React.FC<{ onLogin: (u: User) => void; onDemo: () => void }> = ({ onLogin, onDemo }) => {
    const navigate = useNavigate();
    return (
        <PricingPage
            onBack={() => navigate('/login')}
            onSelectPlan={() => {
                // All plans are free — go straight to register form
                navigate('/login', { state: { register: true } });
            }}
        />
    );
};

// ─────────────────────────────────────────────
// LOGIN ROUTE
// ─────────────────────────────────────────────
const LoginRoute: React.FC<{ onLogin: (u: User) => void; onDemo: () => void }> = ({ onLogin, onDemo }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const defaultRegister = (location.state as any)?.register === true;
    return (
        <LoginPage
            onLogin={onLogin}
            onBack={() => navigate('/')}
            onDemo={onDemo}
            onSignUp={() => navigate('/pricing')}
            defaultRegister={defaultRegister}
        />
    );
};

// ─────────────────────────────────────────────
// APP ROUTE — authenticated shell at /app
// ─────────────────────────────────────────────
const AppRoute: React.FC<{
    user: User;
    onLogout: () => void;
    onLoginRedirect: () => void;
    onGuestSignIn: () => void;
    onGuestSignUp: () => void;
}> = ({ user, onLogout, onLoginRedirect, onGuestSignIn, onGuestSignUp }) => {
    const [activePage, setActivePage] = useState<AppPage>('dashboard');
    const [selectedProject, setSelectedProject] = useState<Project | null>(null);
    const [selectedDataset, setSelectedDataset] = useState<Dataset | null>(null);
    const [projects, setProjects] = useState<Project[]>([]);
    const [projectsLoading, setProjectsLoading] = useState(false);
    const [showPasswordChange, setShowPasswordChange] = useState(false);

    const isGuest = user.role === 'guest' || user.username.startsWith('guest_');

    useEffect(() => {
        const fetchProjects = async () => {
            setProjectsLoading(true);
            try {
                const res = await fetch('/api/projects', { credentials: 'include' });
                if (res.ok) setProjects(await res.json());
            } catch {}
            finally { setProjectsLoading(false); }
        };
        fetchProjects();
    }, []);

    const renderPageContent = () => {
        if (showPasswordChange) {
            return (
                <PageContent title="Change Password">
                    <PasswordChange onClose={() => setShowPasswordChange(false)} />
                </PageContent>
            );
        }

        switch (activePage) {
            case 'dashboard':
                return (
                    <DashboardPage
                        username={user.username}
                        isGuest={isGuest}
                        projects={projects}
                        isLoading={projectsLoading}
                        onCreateProject={() => setActivePage('projects')}
                        onOpenProject={(project) => { setSelectedProject(project); setActivePage('datasets'); }}
                        onNavigate={(page) => setActivePage(page as AppPage)}
                        onOpenSample={() => setActivePage('demo' as AppPage)}
                        onSignUp={onLoginRedirect}
                    />
                );

            case 'projects':
                return (
                    <PageContent title="Projects" subtitle="Organize your datasets into projects">
                        <ProjectManager
                            onProjectSelect={(project) => {
                                if (!project) return;
                                if (isGuest && project.name === 'Sample Sales Data') {
                                    setActivePage('demo' as AppPage);
                                    return;
                                }
                                setSelectedProject(project);
                                setActivePage('datasets');
                            }}
                            selectedProject={selectedProject}
                            isGuest={isGuest}
                            onOpenSample={() => setActivePage('demo' as AppPage)}
                            onSignUp={onLoginRedirect}
                        />
                    </PageContent>
                );

            case 'datasets': {
                if (!selectedProject) { setActivePage('projects'); return null; }
                if (isGuest && selectedProject.name === 'Sample Sales Data') {
                    return (
                        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                            <DemoDatasetPage onSignUp={onLoginRedirect} onBack={() => setActivePage('dashboard')} />
                        </Box>
                    );
                }
                return (
                    <PageContent title={selectedProject.name} subtitle="Your spreadsheets and CSVs in this project">
                        <DatasetManager
                            project={selectedProject}
                            onDatasetSelect={(dataset) => {
                                if (dataset === null) { setSelectedProject(null); setActivePage('projects'); }
                                else { setSelectedDataset(dataset); setActivePage('explore'); }
                            }}
                            selectedDataset={selectedDataset}
                            onNavigateToDataManipulation={(dataset) => { setSelectedDataset(dataset); setActivePage('explore'); }}
                            onNavigateToAnalysis={(dataset) => { setSelectedDataset(dataset); setActivePage('explore'); }}
                            onNavigateToChatWithData={(dataset) => { setSelectedDataset(dataset); setActivePage('explore'); }}
                        />
                    </PageContent>
                );
            }

            case 'demo':
                return (
                    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                        <DemoDatasetPage onSignUp={onLoginRedirect} onBack={() => setActivePage('dashboard')} />
                    </Box>
                );

            case 'explore':
            case 'overview':
            case 'analysis':
            case 'chat':
            case 'manipulation':
                if (!selectedDataset || !selectedProject) { setActivePage('projects'); return null; }
                return (
                    <ExplorePage
                        project={selectedProject}
                        dataset={selectedDataset}
                        onBack={() => setActivePage('datasets')}
                    />
                );

            case 'admin':
                if (user.role !== 'admin') { setActivePage('dashboard'); return null; }
                return (
                    <PageContent title="Admin" subtitle="Manage users and system settings">
                        <AdminSetupPage onBack={() => setActivePage('dashboard')} />
                    </PageContent>
                );

            default:
                return null;
        }
    };

    return (
        <AppShell
            activePage={activePage}
            onNavigate={setActivePage}
            onLogout={onLogout}
            username={user.username}
            projectName={selectedProject?.name}
            datasetName={selectedDataset?.name}
            isAdmin={user.role === 'admin'}
            hasDataset={!!selectedDataset}
            isGuest={isGuest}
            onGuestSignIn={onGuestSignIn}
            onGuestSignUp={onGuestSignUp}
            projects={projects}
            selectedProjectId={selectedProject?.id}
            onOpenProject={(project) => { setSelectedProject(project as any); setActivePage('datasets'); }}
        >
            {renderPageContent()}
        </AppShell>
    );
};

// ─────────────────────────────────────────────
// MAIN APP — session management + routing
// ─────────────────────────────────────────────
const App: React.FC = () => {
    const [user, setUser] = useState<User | null>(null);
    const [initialLoading, setInitialLoading] = useState(true);
    const navigate = useNavigate();

    // Check for existing session on mount
    useEffect(() => {
        const checkSession = async () => {
            try {
                const res = await fetch('/api/me', { credentials: 'include' });
                if (res.ok) {
                    const u = await res.json();
                    setUser(u);
                    // Only auto-redirect from /login — landing page always stays at /
                    if (window.location.pathname === '/login') {
                        navigate('/app', { replace: true });
                    }
                }
            } catch {}
            finally { setInitialLoading(false); }
        };
        checkSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleLogin = (u: User) => {
        setUser(u);
        navigate('/app', { replace: true });
    };

    const handleGuestAccess = async () => {
        try {
            const res = await fetch('/api/guest-access', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
            });
            if (res.ok) {
                const data = await res.json();
                const guestUser = data.user ?? { id: -1, username: data.username ?? 'Guest', role: 'guest' };
                handleLogin(guestUser);
                return;
            }
        } catch {}
        navigate('/login');
    };

    const handleLogout = async () => {
        await fetch('/api/logout', { method: 'POST', credentials: 'include' });
        setUser(null);
        navigate('/', { replace: true });
    };

    // Guest-specific: sign out of guest mode then redirect
    const handleGuestToSignIn = async () => {
        await fetch('/api/logout', { method: 'POST', credentials: 'include' });
        setUser(null);
        navigate('/login', { replace: true });
    };

    const handleGuestToSignUp = async () => {
        await fetch('/api/logout', { method: 'POST', credentials: 'include' });
        setUser(null);
        navigate('/pricing', { replace: true });
    };

    const handleDemo = () => {
        // Guest demo without backend — create a local guest user
        setUser({ id: -1, username: 'Guest', role: 'guest' });
        navigate('/app', { replace: true });
    };

    if (initialLoading) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
                <CircularProgress size={24} sx={{ color: '#111111' }} />
            </Box>
        );
    }

    return (
        <Routes>
            {/* Public landing page — always accessible */}
            <Route path="/" element={<LandingRoute />} />

            {/* Pricing page — public */}
            <Route path="/pricing" element={<PricingRoute onLogin={handleLogin} onDemo={handleDemo} />} />

            {/* Login / register */}
            <Route path="/login" element={
                user
                    ? <Navigate to="/app" replace />
                    : <LoginRoute onLogin={handleLogin} onDemo={handleDemo} />
            } />

            {/* Authenticated app shell */}
            <Route path="/app" element={
                user
                    ? <AppRoute user={user} onLogout={handleLogout} onLoginRedirect={() => navigate('/pricing')} onGuestSignIn={handleGuestToSignIn} onGuestSignUp={handleGuestToSignUp} />
                    : <Navigate to="/login" replace />
            } />

            {/* Catch-all → landing */}
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
};

export default App;
